import { type ReactNode, useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Button, Card, Icon, Modal } from "semantic-ui-react";
import styled from "styled-components";

import { pluralise } from "simplydo/core/helpers";
import { FileChooserDropzoneTrigger } from "simplydo/react/components/Choosers/FileChooser/FileChooserDropzoneTrigger";
import {
  type OnCompleteParams,
  allowedImageTypesList,
} from "simplydo/react/components/Choosers/FileChooser/uploadUtils";

import { BodySegment } from "components/ideas/Body/Custom/styles";
import { ImageChooser } from "components/lib/Choosers";

const imageHeight = 250;
const fullImageHeight = 365;

const GalleryImages = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex: 1;
`;

const GalleryImage = styled.img<{ $active: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 10px;
  margin: 3px;
  object-fit: cover;
  ${({ theme, $active }) =>
    $active && `border: 3px solid ${theme.primaryColour}`};
  cursor: pointer;
`;

const NoActiveImage = styled.div<{ $imageHeight: string; $imageWidth: string }>`
  border-radius: 10px;
  height: ${({ $imageHeight }) => $imageHeight};
  width: ${({ $imageWidth }) => $imageWidth};
  border: 1px solid lightgray;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 3px;
`;

type GalleryProps = {
  images: Array<{ _id: string; url: string; name: string }>;
  getFileUrl?: (file: any) => string;
  onRemove?: (fileId: string) => void;
};

const Gallery = ({ images, getFileUrl, onRemove }: GalleryProps) => {
  const { t } = useTranslation();

  const [activeImage, setActiveImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  useEffect(() => {
    if (images) {
      setActiveImage(images[0]);
    } else setActiveImage(null);
  }, [images]);

  const handleEnterPress = useCallback((event) => {
    if (event.keyCode === 13) {
      setImageModalOpen(true);
    }
  }, []);

  return (
    <>
      <Card.Group centered onClick={(e) => e.stopPropagation()}>
        <Card style={{ width: "80%" }}>
          <Card.Content style={{ color: "#808080" }}>
            <Icon name="images outline" />
            {pluralise(images.length, "uploaded image", "uploaded images")}
          </Card.Content>
          <Card.Content textAlign="center">
            {activeImage ?
              <div style={{ margin: 3, flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    backgroundColor: "#f8f8f8",
                    borderRadius: 15,
                  }}
                >
                  <img
                    tabIndex={0}
                    src={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                    alt={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                    onClick={() => {
                      setImageModalOpen(true);
                    }}
                    onKeyDown={(e) => handleEnterPress(e)}
                    style={{
                      height: fullImageHeight,
                      width: "100%",
                      objectFit: "contain",
                      borderRadius: 10,
                      cursor: "default",
                    }}
                  />
                </div>
              </div>
            : <NoActiveImage
                $imageHeight={`${imageHeight + 40}px`}
                $imageWidth={"100%"}
              >
                <h5>
                  {!images.length ?
                    t("multiimage.upload.none")
                  : "Select an image"}
                </h5>
              </NoActiveImage>
            }
            <GalleryImages>
              {images && Array.isArray(images) && images.length ?
                images.map((s) => (
                  <GalleryImage
                    src={getFileUrl ? getFileUrl(s) : s.url}
                    key={s._id}
                    onClick={() => setActiveImage(s)}
                    $active={activeImage?._id === s._id}
                  />
                ))
              : null}
            </GalleryImages>
          </Card.Content>
          {onRemove ?
            <Card.Content
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "12px 16px",
              }}
            >
              <Button
                basic
                icon="trash"
                onClick={() => {
                  onRemove(activeImage._id);
                }}
                size="tiny"
                style={{ marginLeft: 0, marginRight: 0 }}
              />
            </Card.Content>
          : null}
        </Card>
      </Card.Group>
      {activeImage ?
        <Modal
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          style={{ maxWidth: "90vw", maxHeight: "90vh" }}
        >
          <Modal.Header> {activeImage.name} </Modal.Header>
          <Modal.Content
            scrolling
            style={{
              display: "flex",
              justifyContent: "center",
              padding: 0,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            {activeImage && (
              <img
                src={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                alt={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                style={{
                  maxWidth: "90%",
                  maxHeight: "60%",
                  objectFit: "contain",
                  marginBottom: 10,
                  marginTop: 10,
                }}
              />
            )}
          </Modal.Content>
          <Modal.Actions>
            {activeImage && (
              <a
                href={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                download={activeImage.name || "image.png"}
              >
                <Button primary content="Download image" icon="download" />
              </a>
            )}
            <Button
              content="Close"
              onClick={() => {
                setImageModalOpen(false);
              }}
            />
          </Modal.Actions>
        </Modal>
      : null}
    </>
  );
};

type DropZoneProps = {
  children: import("react").ReactNode;
  getFileUrl: (file: {
    _id: string;
    name: string;
    value: string;
    url: string;
  }) => string;
  images: Array<{ _id: string; name: string; value: string; url: string }>;
  onRemove: (fileId: string) => void;
  onClick: () => void;
};

const DropZone = ({
  children,
  images,
  getFileUrl,
  onClick,
  onRemove,
}: DropZoneProps) => {
  return (
    <BodySegment onClick={onClick}>
      {children}
      {images?.length > 0 ?
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <Gallery
            images={images}
            getFileUrl={getFileUrl}
            onRemove={onRemove}
          />
        </div>
      : null}
    </BodySegment>
  );
};

type MultiImageChooserProps = {
  images: Array<{ _id: string; name: string; value: string; url: string }>;
  onComplete: (files: OnCompleteParams[]) => void;
  deleteFile: (fileId: string) => void;
  getFileUrl?: (file: {
    _id: string;
    name: string;
    value: string;
    url: string;
  }) => string;
  forType: string;
  forId: string;
  editing?: boolean;
  maxImages?: number;
  showGallery?: boolean;
};

const MultiImageChooser = ({
  images = [],
  onComplete,
  deleteFile,
  forType,
  forId,
  editing,
  getFileUrl,
  maxImages,
  showGallery = true,
}: MultiImageChooserProps) => {
  if (!editing && !images.length) return null;

  if (!editing) {
    return <Gallery images={images} getFileUrl={getFileUrl} />;
  }

  const MultiImageDropzone = ({
    children,
    onClick,
  }: {
    children: ReactNode;
    onClick: () => void;
  }) => (
    <DropZone
      images={images}
      getFileUrl={getFileUrl}
      onClick={onClick}
      onRemove={deleteFile}
    >
      {children}
    </DropZone>
  );

  return (
    <ImageChooser
      allowMultiple={true}
      onCompleteMultiple={onComplete}
      forType={forType}
      forId={forId}
      trigger={
        <FileChooserDropzoneTrigger
          forType={forType}
          forId={forId}
          accept={allowedImageTypesList.join(",")}
          allowMultiple={true}
          onCompleteMultiple={onComplete}
          dropzoneProps={{
            title: "Upload multiple images",
            subtitle:
              maxImages &&
              `Maximum of ${pluralise(maxImages, "image", "images")}`,
            DropZoneComponent: showGallery ? MultiImageDropzone : BodySegment,
            defaultIcon: "images",
          }}
        />
      }
    />
  );
};

export default MultiImageChooser;
